// Mock data for Fresh Bites Group Ltd website

export const heroData = {
  title: "We turn your recipe into reality",
  subtitle: "Manufacturing, scaling and bringing your brand to market",
  description: "Partner with a UK food manufacturer that turns authentic traditional recipes into scalable, healthy commercial products.",
  image: "https://customer-assets.emergentagent.com/job_freshbites-scale/artifacts/ojupry0m_fiery-culinary-drama-stockcake.jpg"
};

export const whyChooseUs = [
  {
    title: "Recipe Development Excellence",
    description: "Transform your traditional recipes into commercially viable products with our expert development team.",
    icon: "ChefHat"
  },
  {
    title: "Quality Manufacturing",
    description: "HACCP certified facilities ensuring the highest food safety and quality standards.",
    icon: "Factory"
  },
  {
    title: "Scalable Solutions",
    description: "From small batches to large commercial production runs, we scale with your brand.",
    icon: "TrendingUp"
  },
  {
    title: "Cultural Authenticity",
    description: "Deep roots in Indian/Telugu cuisine, preserving heritage while meeting modern standards.",
    icon: "Heart"
  }
];

export const services = [
  {
    id: 1,
    title: "Recipe Development",
    description: "We refine and optimize your recipes for commercial production, ensuring cost-effectiveness without compromising on taste or quality.",
    features: ["Recipe refinement", "Cost optimization", "Feasibility analysis", "Nutritional profiling"],
    image: "https://images.unsplash.com/photo-1577219492769-b63a779fac28?w=800&q=80"
  },
  {
    id: 2,
    title: "Product Scaling",
    description: "Scale your production from small batches to larger commercial runs while maintaining consistency and quality.",
    features: ["Small-batch production", "Commercial scaling", "Consistency testing", "Production optimization"],
    image: "https://images.unsplash.com/photo-1588416820614-f8d6ac6cea56?w=800&q=80"
  },
  {
    id: 3,
    title: "Quality & Compliance",
    description: "Full compliance with UK food safety standards, including HACCP certification and complete ingredient traceability.",
    features: ["HACCP compliance", "Food safety audits", "Ingredient traceability", "Quality assurance"],
    image: "https://images.unsplash.com/photo-1621494548002-bfc916172ead?w=800&q=80"
  },
  {
    id: 4,
    title: "Packaging & Logistics",
    description: "Complete packaging solutions and logistics support to get your products market-ready and delivered.",
    features: ["Custom packaging design", "Label compliance", "Distribution support", "Logistics coordination"],
    image: "https://images.unsplash.com/photo-1589109807644-924edf14ee09?w=800&q=80"
  }
];

export const workProcess = [
  {
    step: 1,
    title: "Discovery & Recipe Review",
    description: "We start by understanding your vision, reviewing your recipe, and assessing market potential.",
    icon: "Search"
  },
  {
    step: 2,
    title: "Feasibility & Cost Evaluation",
    description: "Detailed analysis of production feasibility, ingredient sourcing, and cost structure.",
    icon: "Calculator"
  },
  {
    step: 3,
    title: "Prototype & Development",
    description: "Creating prototypes, refining recipes, and conducting taste tests to perfect your product.",
    icon: "Beaker"
  },
  {
    step: 4,
    title: "Pilot Manufacturing",
    description: "Small-batch production runs to validate processes and ensure quality consistency.",
    icon: "TestTube"
  },
  {
    step: 5,
    title: "Scale & Launch",
    description: "Full-scale production, packaging, and distribution support to bring your product to market.",
    icon: "Rocket"
  }
];

export const partners = [
  {
    name: "The Product of Andhra",
    location: "Birmingham",
    description: "Authentic Andhra cuisine brought to Birmingham's food scene with our manufacturing partnership.",
    image: "https://images.unsplash.com/photo-1622021142947-da7dedc7c39a?w=600&q=80",
    testimonial: "Fresh Bites helped us scale our traditional recipes while maintaining authenticity."
  },
  {
    name: "Vedique Resto Bar",
    location: "Birmingham",
    description: "Modern Indian dining experience powered by traditional manufacturing excellence.",
    image: "https://images.pexels.com/photos/32742915/pexels-photo-32742915.jpeg?w=600&q=80",
    testimonial: "Their expertise in traditional Indian recipes is unmatched. Quality is always consistent."
  },
  {
    name: "Daily Crop",
    location: "UK Distribution Partner",
    description: "National distribution network helping brands reach customers across the UK.",
    image: "https://images.unsplash.com/photo-1589109807644-924edf14ee09?w=600&q=80",
    testimonial: "Reliable manufacturing partner for our distribution network."
  }
];

export const careers = [
  {
    id: 1,
    title: "Development Chef",
    department: "Product Development",
    location: "Birmingham",
    type: "Full-time",
    description: "Lead recipe development and product innovation for traditional and heritage food brands.",
    requirements: ["5+ years experience in food development", "Strong knowledge of Indian/Telugu cuisine", "Food safety certifications"],
    image: "https://images.pexels.com/photos/32742922/pexels-photo-32742922.jpeg?w=600&q=80"
  },
  {
    id: 2,
    title: "Head Chef",
    department: "Production",
    location: "Birmingham",
    type: "Full-time",
    description: "Oversee production operations and ensure quality standards across all manufacturing processes.",
    requirements: ["10+ years culinary experience", "Team management skills", "HACCP knowledge"],
    image: "https://images.pexels.com/photos/5953503/pexels-photo-5953503.jpeg?w=600&q=80"
  },
  {
    id: 3,
    title: "HSE Manager",
    department: "Health, Safety & Environment",
    location: "Birmingham",
    type: "Full-time",
    description: "Lead health, safety and environmental compliance across our manufacturing facilities.",
    requirements: ["HSE qualifications", "Food manufacturing experience", "Audit management"],
    image: "https://images.unsplash.com/photo-1621494547944-5ddbc84514b2?w=600&q=80"
  },
  {
    id: 4,
    title: "Operations Manager",
    department: "Operations",
    location: "Birmingham",
    type: "Full-time",
    description: "Manage day-to-day operations, production planning, and team coordination.",
    requirements: ["Operations management experience", "Food production knowledge", "Strong leadership"],
    image: "https://images.pexels.com/photos/13195436/pexels-photo-13195436.jpeg?w=600&q=80"
  }
];

export const aboutContent = {
  mission: "To help food brands grow by turning their recipes into scalable, healthy products — just like the food our grandparents used to eat — and serve them across retail, foodservice and online platforms.",
  vision: "We develop and manufacture your recipe so you can focus on growing your brand and serving customers with high-quality, nutritious food.",
  values: [
    {
      title: "Cultural Heritage",
      description: "Preserving traditional culinary methods while meeting modern standards",
      icon: "Globe"
    },
    {
      title: "Quality First",
      description: "Uncompromising commitment to food safety and product excellence",
      icon: "Award"
    },
    {
      title: "Innovation",
      description: "Combining traditional recipes with modern manufacturing technology",
      icon: "Lightbulb"
    },
    {
      title: "Partnership",
      description: "Growing together with emerging food brands and entrepreneurs",
      icon: "Users"
    }
  ],
  story: "Fresh Bites Group Ltd was founded with a vision to bridge the gap between authentic traditional recipes and commercial food production. With deep roots in Indian and Telugu cuisine, we understand the importance of preserving cultural culinary heritage while meeting the demands of modern food manufacturing. Our Birmingham-based facilities combine traditional cooking methods with state-of-the-art production technology.",
  images: [
    "https://images.unsplash.com/photo-1760907949894-b66d728e0a8e?w=800&q=80",
    "https://images.unsplash.com/photo-1573996744014-66646f5a6202?w=800&q=80",
    "https://images.pexels.com/photos/35105802/pexels-photo-35105802.jpeg?w=800&q=80"
  ]
};

export const contactInfo = {
  kitchen: {
    name: "Production Facility",
    address: "Kitchen 4, Griller Kitchens, 121 Miller St",
    city: "Birmingham B6 4NG",
    country: "United Kingdom"
  },
  office: {
    name: "Head Office",
    address: "706 Canterbury House",
    city: "Birmingham B31DB",
    country: "United Kingdom"
  },
  company: {
    name: "Fresh Bites Group Ltd",
    registration: "Company No: 15757659",
    email: "are@freshbitesgroup.com",
    phone: "+44 7769476968"
  }
};

export const faqData = [
  {
    question: "What types of food products can you manufacture?",
    answer: "We specialize in traditional and heritage foods, particularly Indian and Telugu cuisine. This includes curries, snacks, ready-to-eat meals, sauces, and specialty items. We work with startup brands to scale their authentic recipes for commercial production."
  },
  {
    question: "What is your minimum order quantity?",
    answer: "We're flexible with order sizes to support growing brands. We can start with small pilot batches and scale up as your brand grows. Contact us to discuss your specific requirements."
  },
  {
    question: "Do you help with recipe development?",
    answer: "Yes! Our development chefs work with you to refine recipes for commercial production, optimize costs, and ensure consistency while maintaining authentic flavors."
  },
  {
    question: "Are you HACCP certified?",
    answer: "Yes, our facilities are fully HACCP certified and comply with all UK food safety standards. We maintain rigorous quality control and traceability throughout the production process."
  },
  {
    question: "Can you help with packaging and labeling?",
    answer: "Absolutely. We provide complete packaging solutions including design support, compliance labeling, and coordination with packaging suppliers to get your product market-ready."
  }
];
